'use client';

import React, { useState } from 'react';
import { ScrollText, AlertTriangle, BarChart2, GitBranch, Download, Filter, CheckCircle2, Clock, Package, TrendingDown, TrendingUp, Minus, Printer, X, FileText, RefreshCw } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


// ─── Types ────────────────────────────────────────────────────────────────────

type ReportType = 'audit-log' | 'recall' | 'inventory-variance' | 'batch-traceability';

interface ReportConfig {
  id: ReportType;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
  count: number;
  lastGenerated: string;
}

interface AuditLogEntry {
  id: string;
  timestamp: string;
  operator: string;
  device: string;
  action: string;
  item: string;
  qty: string;
  location: string;
  grnRef: string;
  batch: string;
}

interface RecallEntry {
  id: string;
  batchNumber: string;
  itemName: string;
  sku: string;
  mfgDate: string;
  expiryDate: string;
  affectedQty: number;
  uom: string;
  grnId: string;
  rackLocation: string;
  status: 'active' | 'dispatched' | 'quarantined';
  kitIds: string[];
}

interface VarianceEntry {
  id: string;
  sku: string;
  itemName: string;
  category: string;
  systemQty: number;
  physicalQty: number;
  variance: number;
  uom: string;
  location: string;
  lastCountDate: string;
  reason: string;
}

interface BatchTraceEntry {
  id: string;
  batchNumber: string;
  itemName: string;
  sku: string;
  mfgDate: string;
  expiryDate: string;
  receivedQty: number;
  pickedQty: number;
  remainingQty: number;
  uom: string;
  grnId: string;
  supplier: string;
  rackLocation: string;
  kitIds: string[];
  dispatchedTo: string;
  status: 'in-stock' | 'partially-dispatched' | 'fully-dispatched';
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const AUDIT_LOGS: AuditLogEntry[] = [
  { id: 'AL-001', timestamp: '20 Jun 2026, 09:42 AM', operator: 'Prasun Sharma', device: 'HH-01', action: 'Kit Completed', item: 'Surgical Dressing Kit — Type A', qty: '1 KIT', location: 'DISPATCH-ZONE', grnRef: 'GRN-20260612-001', batch: 'BAT-2026-001' },
  { id: 'AL-002', timestamp: '20 Jun 2026, 09:15 AM', operator: 'Riya Mehta', device: 'HH-02', action: 'Item Picked', item: 'Non Woven Tape 75mm', qty: '2 ROLL', location: 'RACK-A3-S2', grnRef: 'GRN-20260610-003', batch: 'BAT-2026-003' },
  { id: 'AL-003', timestamp: '20 Jun 2026, 08:50 AM', operator: 'Arjun Nair', device: 'HH-03', action: 'QC Approved', item: 'Sterile Gauze 10x10cm', qty: '50 PCS', location: 'QC-ZONE', grnRef: 'GRN-20260619-007', batch: 'BAT-2026-007' },
  { id: 'AL-004', timestamp: '20 Jun 2026, 08:30 AM', operator: 'Prasun Sharma', device: 'HH-01', action: 'Rack Slotted', item: 'Elastic Bandage 6cm', qty: '30 ROLL', location: 'RACK-B1-S4', grnRef: 'GRN-20260618-005', batch: 'BAT-2026-005' },
  { id: 'AL-005', timestamp: '19 Jun 2026, 05:10 PM', operator: 'Riya Mehta', device: 'HH-02', action: 'GRN Created', item: 'Disposable Gloves L', qty: '200 PCS', location: 'INBOUND-DOCK', grnRef: 'GRN-20260619-009', batch: 'BAT-2026-009' },
  { id: 'AL-006', timestamp: '19 Jun 2026, 04:45 PM', operator: 'Arjun Nair', device: 'HH-03', action: 'QC Rejected', item: 'Cotton Wool 500g', qty: '10 PKT', location: 'QC-ZONE', grnRef: 'GRN-20260619-008', batch: 'BAT-2026-008' },
  { id: 'AL-007', timestamp: '19 Jun 2026, 03:20 PM', operator: 'Prasun Sharma', device: 'HH-01', action: 'Item Dispatched', item: 'Surgical Dressing Kit — Type B', qty: '1 KIT', location: 'DISPATCH-ZONE', grnRef: 'GRN-20260611-002', batch: 'BAT-2026-002' },
  { id: 'AL-008', timestamp: '19 Jun 2026, 02:00 PM', operator: 'Riya Mehta', device: 'HH-02', action: 'Item Added', item: 'Adhesive Plaster 72x19mm', qty: '100 PCS', location: 'INBOUND-DOCK', grnRef: 'GRN-20260619-010', batch: 'BAT-2026-010' },
];

const RECALL_ENTRIES: RecallEntry[] = [
  { id: 'RC-001', batchNumber: 'BAT-2026-008', itemName: 'Cotton Wool 500g', sku: 'SKU-CW-500', mfgDate: '01 Mar 2026', expiryDate: '01 Jul 2026', affectedQty: 10, uom: 'PKT', grnId: 'GRN-20260619-008', rackLocation: 'QUARANTINE', status: 'quarantined', kitIds: [] },
  { id: 'RC-002', batchNumber: 'BAT-2026-003', itemName: 'Non Woven Tape 75mm', sku: 'SKU-NWT-75', mfgDate: '15 Jan 2026', expiryDate: '15 Jul 2026', affectedQty: 8, uom: 'ROLL', grnId: 'GRN-20260610-003', rackLocation: 'RACK-A3-S2', status: 'active', kitIds: ['KIT-20260612-001', 'KIT-20260611-045'] },
  { id: 'RC-003', batchNumber: 'BAT-2026-001', itemName: 'Sterile Gauze 10x10cm', sku: 'SKU-SG-10', mfgDate: '10 Dec 2025', expiryDate: '10 Jun 2026', affectedQty: 20, uom: 'PCS', grnId: 'GRN-20260601-001', rackLocation: 'DISPATCHED', status: 'dispatched', kitIds: ['KIT-20260601-012'] },
];

const VARIANCE_ENTRIES: VarianceEntry[] = [
  { id: 'VAR-001', sku: 'SKU-NWT-75', itemName: 'Non Woven Tape 75mm x 250cm', category: 'Wound Care', systemQty: 45, physicalQty: 43, variance: -2, uom: 'ROLL', location: 'RACK-A3-S2', lastCountDate: '18 Jun 2026', reason: 'Picking discrepancy' },
  { id: 'VAR-002', sku: 'SKU-EB-6', itemName: 'Elastic Bandage 6cm', category: 'Bandages', systemQty: 30, physicalQty: 32, variance: +2, uom: 'ROLL', location: 'RACK-B1-S4', lastCountDate: '18 Jun 2026', reason: 'GRN over-receipt' },
  { id: 'VAR-003', sku: 'SKU-DG-L', itemName: 'Disposable Gloves L', category: 'PPE', systemQty: 200, physicalQty: 195, variance: -5, uom: 'PCS', location: 'RACK-C2-S1', lastCountDate: '17 Jun 2026', reason: 'Damaged units removed' },
  { id: 'VAR-004', sku: 'SKU-AP-72', itemName: 'Adhesive Plaster 72x19mm', category: 'Wound Care', systemQty: 100, physicalQty: 100, variance: 0, uom: 'PCS', location: 'RACK-A1-S3', lastCountDate: '17 Jun 2026', reason: 'No variance' },
  { id: 'VAR-005', sku: 'SKU-SG-10', itemName: 'Sterile Gauze 10x10cm', category: 'Wound Care', systemQty: 50, physicalQty: 47, variance: -3, uom: 'PCS', location: 'RACK-A2-S1', lastCountDate: '16 Jun 2026', reason: 'Unrecorded pick' },
];

const BATCH_TRACE_ENTRIES: BatchTraceEntry[] = [
  { id: 'BT-001', batchNumber: 'BAT-2026-001', itemName: 'Non Woven Tape 75mm x 250cm', sku: 'SKU-NWT-75', mfgDate: '01 Jan 2026', expiryDate: '01 Jun 2027', receivedQty: 50, pickedQty: 7, remainingQty: 43, uom: 'ROLL', grnId: 'GRN-20260612-001', supplier: 'MedSupply India Pvt Ltd', rackLocation: 'RACK-A3-S2', kitIds: ['KIT-20260612-001', 'KIT-20260611-045'], dispatchedTo: 'Apollo Hospital, Delhi', status: 'partially-dispatched' },
  { id: 'BT-002', batchNumber: 'BAT-2026-003', itemName: 'Elastic Bandage 6cm', sku: 'SKU-EB-6', mfgDate: '15 Feb 2026', expiryDate: '15 Feb 2028', receivedQty: 30, pickedQty: 0, remainingQty: 30, uom: 'ROLL', grnId: 'GRN-20260618-005', supplier: 'BandageCo Ltd', rackLocation: 'RACK-B1-S4', kitIds: [], dispatchedTo: '—', status: 'in-stock' },
  { id: 'BT-003', batchNumber: 'BAT-2026-002', itemName: 'Sterile Gauze 10x10cm', sku: 'SKU-SG-10', mfgDate: '10 Dec 2025', expiryDate: '10 Dec 2026', receivedQty: 100, pickedQty: 100, remainingQty: 0, uom: 'PCS', grnId: 'GRN-20260601-001', supplier: 'SterileWorks Pvt Ltd', rackLocation: 'DISPATCHED', kitIds: ['KIT-20260601-012', 'KIT-20260601-013'], dispatchedTo: 'Fortis Hospital, Mumbai', status: 'fully-dispatched' },
];

// ─── Report Configs ────────────────────────────────────────────────────────────

const REPORT_CONFIGS: ReportConfig[] = [
  {
    id: 'audit-log',
    label: 'Audit Log Report',
    description: 'Full timestamped log of all warehouse actions by operator, device, and action type',
    icon: ScrollText,
    color: 'text-primary',
    bgColor: 'bg-primary/10',
    borderColor: 'border-primary/20',
    count: AUDIT_LOGS.length,
    lastGenerated: '19 Jun 2026, 06:00 PM',
  },
  {
    id: 'recall',
    label: 'Recall Report',
    description: 'Affected batches, kit linkages, and dispatch trail for post-shipment recall investigations',
    icon: AlertTriangle,
    color: 'text-red-400',
    bgColor: 'bg-red-500/10',
    borderColor: 'border-red-500/20',
    count: RECALL_ENTRIES.length,
    lastGenerated: '18 Jun 2026, 02:30 PM',
  },
  {
    id: 'inventory-variance',
    label: 'Inventory Variance Report',
    description: 'System vs physical count discrepancies by SKU, location, and cycle count date',
    icon: BarChart2,
    color: 'text-accent',
    bgColor: 'bg-accent/10',
    borderColor: 'border-accent/20',
    count: VARIANCE_ENTRIES.length,
    lastGenerated: '18 Jun 2026, 11:00 AM',
  },
  {
    id: 'batch-traceability',
    label: 'Batch Traceability Report',
    description: 'End-to-end batch journey from GRN receipt through kitting to dispatch destination',
    icon: GitBranch,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-500/10',
    borderColor: 'border-emerald-500/20',
    count: BATCH_TRACE_ENTRIES.length,
    lastGenerated: '17 Jun 2026, 04:15 PM',
  },
];

// ─── PDF Print Helper ──────────────────────────────────────────────────────────

function triggerPrint(reportId: ReportType, title: string) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;

  let tableHtml = '';

  if (reportId === 'audit-log') {
    tableHtml = `
      <table>
        <thead><tr><th>Timestamp</th><th>Operator</th><th>Device</th><th>Action</th><th>Item</th><th>Qty</th><th>Location</th><th>GRN Ref</th><th>Batch</th></tr></thead>
        <tbody>${AUDIT_LOGS.map(r => `<tr><td>${r.timestamp}</td><td>${r.operator}</td><td>${r.device}</td><td>${r.action}</td><td>${r.item}</td><td>${r.qty}</td><td>${r.location}</td><td>${r.grnRef}</td><td>${r.batch}</td></tr>`).join('')}</tbody>
      </table>`;
  } else if (reportId === 'recall') {
    tableHtml = `
      <table>
        <thead><tr><th>Batch No.</th><th>Item Name</th><th>SKU</th><th>Mfg Date</th><th>Expiry Date</th><th>Affected Qty</th><th>GRN ID</th><th>Location</th><th>Status</th><th>Linked Kits</th></tr></thead>
        <tbody>${RECALL_ENTRIES.map(r => `<tr><td>${r.batchNumber}</td><td>${r.itemName}</td><td>${r.sku}</td><td>${r.mfgDate}</td><td>${r.expiryDate}</td><td>${r.affectedQty} ${r.uom}</td><td>${r.grnId}</td><td>${r.rackLocation}</td><td>${r.status.toUpperCase()}</td><td>${r.kitIds.join(', ') || '—'}</td></tr>`).join('')}</tbody>
      </table>`;
  } else if (reportId === 'inventory-variance') {
    tableHtml = `
      <table>
        <thead><tr><th>SKU</th><th>Item Name</th><th>Category</th><th>System Qty</th><th>Physical Qty</th><th>Variance</th><th>UOM</th><th>Location</th><th>Last Count</th><th>Reason</th></tr></thead>
        <tbody>${VARIANCE_ENTRIES.map(r => `<tr><td>${r.sku}</td><td>${r.itemName}</td><td>${r.category}</td><td>${r.systemQty}</td><td>${r.physicalQty}</td><td style="color:${r.variance < 0 ? '#ef4444' : r.variance > 0 ? '#22c55e' : '#6b7280'}">${r.variance > 0 ? '+' : ''}${r.variance}</td><td>${r.uom}</td><td>${r.location}</td><td>${r.lastCountDate}</td><td>${r.reason}</td></tr>`).join('')}</tbody>
      </table>`;
  } else if (reportId === 'batch-traceability') {
    tableHtml = `
      <table>
        <thead><tr><th>Batch No.</th><th>Item Name</th><th>SKU</th><th>Mfg Date</th><th>Expiry Date</th><th>Received</th><th>Picked</th><th>Remaining</th><th>GRN ID</th><th>Supplier</th><th>Location</th><th>Linked Kits</th><th>Dispatched To</th><th>Status</th></tr></thead>
        <tbody>${BATCH_TRACE_ENTRIES.map(r => `<tr><td>${r.batchNumber}</td><td>${r.itemName}</td><td>${r.sku}</td><td>${r.mfgDate}</td><td>${r.expiryDate}</td><td>${r.receivedQty}</td><td>${r.pickedQty}</td><td>${r.remainingQty}</td><td>${r.grnId}</td><td>${r.supplier}</td><td>${r.rackLocation}</td><td>${r.kitIds.join(', ') || '—'}</td><td>${r.dispatchedTo}</td><td>${r.status.replace(/-/g, ' ').toUpperCase()}</td></tr>`).join('')}</tbody>
      </table>`;
  }

  const now = new Date().toLocaleString('en-IN');
  printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>${title}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Segoe UI', Arial, sans-serif; font-size: 11px; color: #111; padding: 24px; }
        .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; padding-bottom: 14px; border-bottom: 2px solid #e5e7eb; }
        .header h1 { font-size: 18px; font-weight: 700; color: #111; }
        .header p { font-size: 11px; color: #6b7280; margin-top: 3px; }
        .meta { text-align: right; font-size: 10px; color: #6b7280; }
        .meta strong { display: block; font-size: 12px; color: #111; margin-bottom: 2px; }
        table { width: 100%; border-collapse: collapse; margin-top: 8px; }
        th { background: #f3f4f6; text-align: left; padding: 7px 9px; font-size: 10px; font-weight: 600; color: #374151; border: 1px solid #e5e7eb; text-transform: uppercase; letter-spacing: 0.04em; }
        td { padding: 6px 9px; border: 1px solid #e5e7eb; color: #374151; vertical-align: top; }
        tr:nth-child(even) td { background: #f9fafb; }
        .footer { margin-top: 20px; padding-top: 12px; border-top: 1px solid #e5e7eb; font-size: 10px; color: #9ca3af; display: flex; justify-content: space-between; }
        @media print { body { padding: 12px; } }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <h1>WMS_Pro — ${title}</h1>
          <p>Regulatory Compliance Report · Generated for post-shipment investigations</p>
        </div>
        <div class="meta">
          <strong>Generated</strong>${now}
        </div>
      </div>
      ${tableHtml}
      <div class="footer">
        <span>WMS_Pro · Compliance Reports Module</span>
        <span>Generated: ${now} · Operator: Prasun Sharma · Device: HH-01</span>
      </div>
    </body>
    </html>
  `);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => { printWindow.print(); }, 400);
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function AuditLogTable({ filters }: { filters: { operator: string; dateFrom: string; dateTo: string; action: string } }) {
  const filtered = AUDIT_LOGS.filter(r => {
    if (filters.operator && !r.operator.toLowerCase().includes(filters.operator.toLowerCase())) return false;
    if (filters.action && r.action !== filters.action) return false;
    return true;
  });
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            {['Timestamp', 'Operator', 'Device', 'Action', 'Item', 'Qty', 'Location', 'GRN Ref', 'Batch'].map(h => (
              <th key={h} className="text-left px-3 py-2.5 font-semibold text-muted-foreground uppercase tracking-wide text-2xs whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {filtered.map((r, i) => (
            <tr key={r.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.timestamp}</td>
              <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{r.operator}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.device}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-2xs font-semibold ${
                  r.action.includes('Rejected') ? 'bg-red-500/15 text-red-400' :
                  r.action.includes('Approved') || r.action.includes('Completed') ? 'bg-emerald-500/15 text-emerald-400' :
                  r.action.includes('Dispatched') ? 'bg-primary/15 text-primary' :
                  'bg-muted text-muted-foreground'
                }`}>{r.action}</span>
              </td>
              <td className="px-3 py-2 text-foreground max-w-[160px] truncate">{r.item}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.qty}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.location}</td>
              <td className="px-3 py-2 font-mono text-xs text-primary">{r.grnRef}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.batch}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {filtered.length === 0 && (
        <div className="py-10 text-center text-muted-foreground text-sm">No records match the current filters.</div>
      )}
    </div>
  );
}

function RecallTable() {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            {['Batch No.', 'Item Name', 'SKU', 'Mfg Date', 'Expiry Date', 'Affected Qty', 'GRN ID', 'Location', 'Status', 'Linked Kits'].map(h => (
              <th key={h} className="text-left px-3 py-2.5 font-semibold text-muted-foreground uppercase tracking-wide text-2xs whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {RECALL_ENTRIES.map((r, i) => (
            <tr key={r.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
              <td className="px-3 py-2 font-mono text-xs font-semibold text-foreground">{r.batchNumber}</td>
              <td className="px-3 py-2 text-foreground">{r.itemName}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.sku}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.mfgDate}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.expiryDate}</td>
              <td className="px-3 py-2 font-semibold text-foreground">{r.affectedQty} {r.uom}</td>
              <td className="px-3 py-2 font-mono text-xs text-primary">{r.grnId}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.rackLocation}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-2xs font-semibold ${
                  r.status === 'quarantined' ? 'bg-red-500/15 text-red-400' :
                  r.status === 'dispatched'? 'bg-accent/15 text-accent' : 'bg-emerald-500/15 text-emerald-400'
                }`}>{r.status.toUpperCase()}</span>
              </td>
              <td className="px-3 py-2 text-muted-foreground">
                {r.kitIds.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {r.kitIds.map(k => <span key={k} className="font-mono text-2xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{k}</span>)}
                  </div>
                ) : <span className="text-muted-foreground/50">—</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function VarianceTable() {
  const totalVariance = VARIANCE_ENTRIES.reduce((s, r) => s + Math.abs(r.variance), 0);
  const negCount = VARIANCE_ENTRIES.filter(r => r.variance < 0).length;
  const posCount = VARIANCE_ENTRIES.filter(r => r.variance > 0).length;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total SKUs Counted', value: VARIANCE_ENTRIES.length, icon: Package, color: 'text-primary', bg: 'bg-primary/10' },
          { label: 'Negative Variance', value: negCount, icon: TrendingDown, color: 'text-red-400', bg: 'bg-red-500/10' },
          { label: 'Positive Variance', value: posCount, icon: TrendingUp, color: 'text-emerald-400', bg: 'bg-emerald-500/10' },
        ].map(s => (
          <div key={s.label} className={`flex items-center gap-3 p-3 rounded-lg border border-border ${s.bg}`}>
            <s.icon size={16} className={s.color} />
            <div>
              <p className="text-lg font-bold text-foreground">{s.value}</p>
              <p className="text-2xs text-muted-foreground">{s.label}</p>
            </div>
          </div>
        ))}
      </div>
      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-muted/50 border-b border-border">
              {['SKU', 'Item Name', 'Category', 'System Qty', 'Physical Qty', 'Variance', 'UOM', 'Location', 'Last Count', 'Reason'].map(h => (
                <th key={h} className="text-left px-3 py-2.5 font-semibold text-muted-foreground uppercase tracking-wide text-2xs whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {VARIANCE_ENTRIES.map((r, i) => (
              <tr key={r.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.sku}</td>
                <td className="px-3 py-2 text-foreground">{r.itemName}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.category}</td>
                <td className="px-3 py-2 text-center font-medium text-foreground">{r.systemQty}</td>
                <td className="px-3 py-2 text-center font-medium text-foreground">{r.physicalQty}</td>
                <td className="px-3 py-2 text-center">
                  <span className={`inline-flex items-center gap-0.5 font-bold ${r.variance < 0 ? 'text-red-400' : r.variance > 0 ? 'text-emerald-400' : 'text-muted-foreground'}`}>
                    {r.variance < 0 ? <TrendingDown size={11} /> : r.variance > 0 ? <TrendingUp size={11} /> : <Minus size={11} />}
                    {r.variance > 0 ? '+' : ''}{r.variance}
                  </span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{r.uom}</td>
                <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.location}</td>
                <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.lastCountDate}</td>
                <td className="px-3 py-2 text-muted-foreground">{r.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function BatchTraceTable() {
  return (
    <div className="overflow-x-auto rounded-lg border border-border">
      <table className="w-full text-xs">
        <thead>
          <tr className="bg-muted/50 border-b border-border">
            {['Batch No.', 'Item Name', 'Mfg Date', 'Expiry Date', 'Received', 'Picked', 'Remaining', 'GRN ID', 'Supplier', 'Location', 'Linked Kits', 'Dispatched To', 'Status'].map(h => (
              <th key={h} className="text-left px-3 py-2.5 font-semibold text-muted-foreground uppercase tracking-wide text-2xs whitespace-nowrap">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {BATCH_TRACE_ENTRIES.map((r, i) => (
            <tr key={r.id} className={`border-b border-border/50 hover:bg-muted/30 transition-colors ${i % 2 === 0 ? '' : 'bg-muted/10'}`}>
              <td className="px-3 py-2 font-mono text-xs font-semibold text-foreground">{r.batchNumber}</td>
              <td className="px-3 py-2 text-foreground max-w-[140px] truncate">{r.itemName}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.mfgDate}</td>
              <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{r.expiryDate}</td>
              <td className="px-3 py-2 text-center font-medium text-foreground">{r.receivedQty}</td>
              <td className="px-3 py-2 text-center font-medium text-accent">{r.pickedQty}</td>
              <td className="px-3 py-2 text-center font-medium text-primary">{r.remainingQty}</td>
              <td className="px-3 py-2 font-mono text-xs text-primary">{r.grnId}</td>
              <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate">{r.supplier}</td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.rackLocation}</td>
              <td className="px-3 py-2">
                {r.kitIds.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {r.kitIds.map(k => <span key={k} className="font-mono text-2xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">{k}</span>)}
                  </div>
                ) : <span className="text-muted-foreground/50">—</span>}
              </td>
              <td className="px-3 py-2 text-muted-foreground max-w-[120px] truncate">{r.dispatchedTo}</td>
              <td className="px-3 py-2">
                <span className={`inline-flex px-2 py-0.5 rounded-full text-2xs font-semibold whitespace-nowrap ${
                  r.status === 'fully-dispatched' ? 'bg-emerald-500/15 text-emerald-400' :
                  r.status === 'partially-dispatched'? 'bg-accent/15 text-accent' : 'bg-primary/15 text-primary'
                }`}>{r.status.replace(/-/g, ' ').toUpperCase()}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Main Board ────────────────────────────────────────────────────────────────

export default function ComplianceReportsBoard() {
  const [activeReport, setActiveReport] = useState<ReportType>('audit-log');
  const [auditFilters, setAuditFilters] = useState({ operator: '', dateFrom: '', dateTo: '', action: '' });
  const [generating, setGenerating] = useState(false);

  const activeConfig = REPORT_CONFIGS.find(r => r.id === activeReport)!;

  const handleGeneratePDF = () => {
    setGenerating(true);
    setTimeout(() => {
      triggerPrint(activeReport, activeConfig.label);
      setGenerating(false);
    }, 600);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Report Type Selector */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {REPORT_CONFIGS.map(cfg => {
          const Icon = cfg.icon;
          const isActive = activeReport === cfg.id;
          return (
            <button
              key={cfg.id}
              onClick={() => setActiveReport(cfg.id)}
              className={`flex flex-col gap-2 p-4 rounded-xl border text-left transition-all duration-150 ${
                isActive
                  ? `${cfg.bgColor} ${cfg.borderColor} ring-1 ring-inset ${cfg.borderColor}`
                  : 'bg-card border-border hover:bg-muted/40'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isActive ? cfg.bgColor : 'bg-muted'}`}>
                  <Icon size={15} className={isActive ? cfg.color : 'text-muted-foreground'} />
                </div>
                {isActive && <CheckCircle2 size={14} className={cfg.color} />}
              </div>
              <div>
                <p className={`text-xs font-semibold leading-tight ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{cfg.label}</p>
                <p className="text-2xs text-muted-foreground mt-0.5">{cfg.count} records</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Active Report Panel */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {/* Panel Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${activeConfig.bgColor}`}>
              <activeConfig.icon size={15} className={activeConfig.color} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">{activeConfig.label}</h2>
              <p className="text-2xs text-muted-foreground">{activeConfig.description}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
              <Clock size={11} />
              <span>Last: {activeConfig.lastGenerated}</span>
            </div>
            <button
              onClick={handleGeneratePDF}
              disabled={generating}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
            >
              {generating ? (
                <><RefreshCw size={12} className="animate-spin" /> Generating…</>
              ) : (
                <><Download size={12} /> Export PDF</>
              )}
            </button>
          </div>
        </div>

        {/* Audit Log Filters */}
        {activeReport === 'audit-log' && (
          <div className="flex flex-wrap items-center gap-2 px-4 py-2.5 border-b border-border bg-muted/20">
            <Filter size={12} className="text-muted-foreground" />
            <input
              type="text"
              placeholder="Filter by operator…"
              value={auditFilters.operator}
              onChange={e => setAuditFilters(f => ({ ...f, operator: e.target.value }))}
              className="h-7 px-2.5 rounded-lg border border-border bg-background text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 w-36"
            />
            <select
              value={auditFilters.action}
              onChange={e => setAuditFilters(f => ({ ...f, action: e.target.value }))}
              className="h-7 px-2 rounded-lg border border-border bg-background text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40"
            >
              <option value="">All Actions</option>
              {['GRN Created', 'Item Added', 'QC Approved', 'QC Rejected', 'Rack Slotted', 'Item Picked', 'Kit Completed', 'Item Dispatched'].map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            {(auditFilters.operator || auditFilters.action) && (
              <button
                onClick={() => setAuditFilters({ operator: '', dateFrom: '', dateTo: '', action: '' })}
                className="flex items-center gap-1 h-7 px-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
              >
                <X size={11} /> Clear
              </button>
            )}
          </div>
        )}

        {/* Table Content */}
        <div className="p-4">
          {activeReport === 'audit-log' && <AuditLogTable filters={auditFilters} />}
          {activeReport === 'recall' && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <AlertTriangle size={14} className="text-red-400 flex-shrink-0" />
                <p className="text-xs text-red-400 font-medium">
                  {RECALL_ENTRIES.filter(r => r.status === 'active').length} batch(es) currently active in warehouse — immediate action may be required.
                </p>
              </div>
              <RecallTable />
            </div>
          )}
          {activeReport === 'inventory-variance' && <VarianceTable />}
          {activeReport === 'batch-traceability' && <BatchTraceTable />}
        </div>

        {/* Panel Footer */}
        <div className="flex items-center justify-between px-4 py-2.5 border-t border-border bg-muted/10">
          <div className="flex items-center gap-1.5 text-2xs text-muted-foreground">
            <FileText size={11} />
            <span>{activeConfig.count} records · Operator: Prasun Sharma · Device: HH-01</span>
          </div>
          <button
            onClick={handleGeneratePDF}
            disabled={generating}
            className="flex items-center gap-1.5 text-2xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Printer size={11} />
            <span>Print / Save PDF</span>
          </button>
        </div>
      </div>
    </div>
  );
}
